import type { Permission } from '@jmc/shared';
import { ROLE_LABELS } from '@jmc/shared';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useI18n, type MessageKey } from '../lib/i18n.js';
import { cx } from './ui.js';
import { DemoBanner } from './DemoBanner.js';

interface NavItem {
  to: string;
  label: MessageKey;
  permission: Permission;
  icon: ReactNode;
}

const icon = (path: ReactNode) => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    {path}
  </svg>
);

const NAV: NavItem[] = [
  {
    to: '/',
    label: 'nav.dashboard',
    permission: 'file:read',
    icon: icon(
      <>
        <rect x="2.75" y="2.75" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="11.25" y="2.75" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="2.75" y="11.25" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <rect x="11.25" y="11.25" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      </>,
    ),
  },
  {
    to: '/search',
    label: 'nav.search',
    permission: 'file:read',
    icon: icon(
      <>
        <circle cx="8.75" cy="8.75" r="5" stroke="currentColor" strokeWidth="1.4" />
        <path d="m12.5 12.5 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </>,
    ),
  },
  {
    to: '/rooms',
    label: 'nav.locations',
    permission: 'file:read',
    icon: icon(
      <>
        <rect x="2.75" y="2.75" width="14.5" height="14.5" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M2.75 7.5h14.5M2.75 12.5h14.5M7.5 2.75v14.5" stroke="currentColor" strokeWidth="1.4" />
      </>,
    ),
  },
  {
    to: '/almirahs',
    label: 'nav.locations2',
    permission: 'file:read',
    icon: icon(
      <>
        <rect x="3.5" y="2.75" width="13" height="14.5" rx="1.3" stroke="currentColor" strokeWidth="1.4" />
        <path d="M3.5 7.25h13M3.5 12.25h13" stroke="currentColor" strokeWidth="1.4" />
        <path d="M9 5h2M9 9.75h2M9 14.5h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </>,
    ),
  },
  {
    to: '/issued',
    label: 'nav.issued',
    permission: 'file:read',
    icon: icon(
      <>
        <path
          d="M5 3.25h6.5L15.5 7v9.25a.75.75 0 0 1-.75.75H5a.75.75 0 0 1-.75-.75V4a.75.75 0 0 1 .75-.75Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M11.25 3.25V7h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      </>,
    ),
  },
  {
    to: '/import',
    label: 'nav.import',
    permission: 'file:import',
    icon: icon(
      <>
        <path d="M10 3v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <path d="m6.75 8.25 3.25 3.25 3.25-3.25" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.5 13.5v2.25a.75.75 0 0 0 .75.75h11.5a.75.75 0 0 0 .75-.75V13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </>,
    ),
  },
  {
    to: '/users',
    label: 'nav.users',
    permission: 'user:read',
    icon: icon(
      <>
        <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.4" />
        <path d="M4.5 16.25a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </>,
    ),
  },
  {
    to: '/reports',
    label: 'nav.reports',
    permission: 'report:read',
    icon: icon(
      <>
        <path d="M4 16V9M10 16V4M16 16v-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </>,
    ),
  },
  {
    to: '/audit',
    label: 'nav.audit',
    permission: 'audit:read',
    icon: icon(
      <>
        <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.4" />
        <path d="M10 6v4.25l2.75 1.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </>,
    ),
  },
];

export function Layout() {
  const { t, lang, setLang, pick } = useI18n();
  const { user, can, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const visible = NAV.filter((item) => can(item.permission));

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[15rem_1fr]">
      {/* Sidebar */}
      <aside
        className={cx(
          'no-print fixed inset-y-0 left-0 z-40 w-60 flex-col bg-navy-800 text-white lg:static lg:flex',
          navOpen ? 'flex' : 'hidden',
        )}
      >
        <div className="flex items-center gap-2.5 px-4 py-4">
          <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden="true" className="shrink-0">
            <rect width="32" height="32" rx="6" fill="#fff" fillOpacity="0.1" />
            <path
              d="M9 8.5h9.5L23 13v10.5a1 1 0 0 1-1 1H10a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1Z"
              fill="none"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path d="M18 8.5V13h5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M12.5 17.5h7M12.5 20.5h4.5" stroke="#F0B429" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <div className="min-w-0 leading-tight">
            <p className="truncate font-semibold">{t('app.name')}</p>
            <p className="truncate text-[0.75rem] text-white/60">{t('app.org')}</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="space-y-0.5">
            {visible.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-2.5 rounded-[5px] px-2.5 py-2 transition-colors',
                      isActive
                        ? 'bg-white/12 font-medium text-white'
                        : 'text-white/70 hover:bg-white/8 hover:text-white',
                    )
                  }
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span className="truncate">{t(item.label)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-white/10 px-3 py-3 text-[0.75rem] text-white/45">
          {t('app.tagline')}
        </div>
      </aside>

      {navOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-col">
        <header className="no-print sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-line bg-surface/95 px-4 py-2.5 backdrop-blur sm:px-6">
          <button
            type="button"
            className="btn-ghost px-2 lg:hidden"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <div
              className="flex rounded-[5px] border border-line-strong p-0.5"
              role="group"
              aria-label={t('common.language')}
            >
              {(['en', 'hi'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLang(option)}
                  aria-pressed={lang === option}
                  className={cx(
                    'rounded-[3px] px-2.5 py-1 text-[0.8125rem] font-medium transition-colors',
                    lang === option
                      ? 'bg-navy-700 text-white'
                      : 'text-ink-500 hover:text-navy-700',
                  )}
                >
                  {option === 'en' ? 'English' : 'हिंदी'}
                </button>
              ))}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="flex items-center gap-2 rounded-[5px] px-2 py-1.5 hover:bg-navy-50"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-navy-700 text-[0.75rem] font-semibold text-white">
                  {(user?.fullName ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden text-left leading-tight sm:block">
                  <span className="block max-w-[11rem] truncate font-medium">
                    {pick(user?.fullName, user?.fullNameHi)}
                  </span>
                  <span className="block text-[0.75rem] text-ink-500">
                    {user ? pick(ROLE_LABELS[user.role].en, ROLE_LABELS[user.role].hi) : ''}
                  </span>
                </span>
              </button>

              {menuOpen && (
                <>
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div
                    role="menu"
                    className="absolute right-0 z-20 mt-1.5 w-56 overflow-hidden rounded-md border border-line bg-white py-1 shadow-lg"
                  >
                    <div className="border-b border-line px-3 py-2">
                      <p className="truncate font-medium">{user?.fullName}</p>
                      <p className="truncate text-[0.8125rem] text-ink-500">
                        @{user?.username}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left hover:bg-navy-50"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/password');
                      }}
                    >
                      {t('password.title')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-red-ink hover:bg-red-bg"
                      onClick={handleSignOut}
                    >
                      {t('action.signOut')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <DemoBanner />

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[82rem]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
